import type { Breadcrumb, ErrorEvent } from '@sentry/react';
import { maskPayLinkString } from './analytics/maskPayLinkTokens';
import { receiptBeforeBreadcrumb, receiptBeforeSend } from './receiptPrivacy';

/**
 * Fail-closed scrubbing for everything Sentry sends.
 *
 * The invariant: no student name, amount, request/response body or bearer
 * credential (`fk=` file tokens, `/pay/<token>` links) reaches Sentry. These
 * hooks are the backstop for call sites that forget it: they drop bodies,
 * console output, non-allow-listed `extra`/`contexts`, DOM attribute text and
 * the text of synthetic (object) non-Error exceptions, and mask tokens in every
 * remaining string. Wire the `safe*` exports: they drop the event if scrubbing
 * throws, so a scrubber bug never sends an unscrubbed event.
 *
 * Scope: the browser SDK in `frontend/` only. Not covered: string/primitive
 * throws (#1451), the message of a genuine `Error` a developer constructs, and
 * the beffe (Python) Sentry SDK. `tags`, `user` and transactions are not
 * scrubbed by an allow-list.
 */

const REDACTED = '[redacted]';
const MAX_DEPTH = 20;
const TRUNCATED = '[Truncated]';

const SENSITIVE_PARAM =
  /^(?:fk|[\w-]*(?:token|secret|key|signature|access)[\w-]*)$/i;
const SENSITIVE_PARAM_IN_TEXT =
  /([?&](?:fk|[\w-]*(?:token|secret|key|signature|access)[\w-]*)=)[^&#\s"']+/gi;

// Default-drop is the point: a field missing from Sentry means it was never
// added to these allow-lists, not that reporting is broken.
const ALLOWED_CONTEXT_KEYS = new Set([
  'app',
  'browser',
  'culture',
  'device',
  'os',
  'react',
  'runtime',
  'trace',
]);
const ATTRIBUTE_TEXT =
  /\[(aria-label|title|alt|name|value|placeholder)="[^"]*"\]/g;
const NON_ERROR_PREFIX = 'Non-Error thrown';
const ALLOWED_EXTRA_KEYS = new Set([
  'url',
  'reasonKey',
  'status',
  'componentStack',
]);
const NETWORK_CRUMB_KEYS = ['method', 'url', 'status_code', 'reason'];

type Plain = Record<string, unknown>;

const isSensitiveParam = (name: string): boolean => {
  let decoded = name;
  try {
    decoded = decodeURIComponent(name);
  } catch {
    // Malformed escape: test the raw name.
  }
  return SENSITIVE_PARAM.test(decoded);
};

export const redactUrl = (
  url: string,
  { blankAllQueryValues = false }: { blankAllQueryValues?: boolean } = {}
): string => {
  const hashAt = url.indexOf('#');
  const fragment = hashAt === -1 ? '' : url.slice(hashAt);
  const beforeFragment = hashAt === -1 ? url : url.slice(0, hashAt);
  const queryAt = beforeFragment.indexOf('?');
  if (queryAt === -1) return maskPayLinkString(beforeFragment) + fragment;

  const path = maskPayLinkString(beforeFragment.slice(0, queryAt));
  const query = beforeFragment
    .slice(queryAt + 1)
    .split('&')
    .map(pair => {
      const eq = pair.indexOf('=');
      if (eq === -1) return blankAllQueryValues && pair ? REDACTED : pair;
      const name = pair.slice(0, eq);
      return blankAllQueryValues || isSensitiveParam(name)
        ? `${name}=${REDACTED}`
        : pair;
    })
    .join('&');
  return `${path}?${query}${fragment}`;
};

const scrubText = (text: string): string =>
  maskPayLinkString(text).replace(SENSITIVE_PARAM_IN_TEXT, `$1${REDACTED}`);

const isPlain = (value: object) => {
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

/** Returns `value` itself when no string inside it changed. */
const mapStrings = (
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>()
): unknown => {
  if (typeof value === 'string') return scrubText(value);
  if (value === null || typeof value !== 'object') return value;
  if (depth > MAX_DEPTH) return TRUNCATED;
  if (seen.has(value)) return value;
  seen.add(value);

  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map(item => {
      const mapped = mapStrings(item, depth + 1, seen);
      if (mapped !== item) changed = true;
      return mapped;
    });
    return changed ? next : value;
  }
  if (!isPlain(value)) return value;

  let changed = false;
  const next: Plain = {};
  for (const [key, item] of Object.entries(value)) {
    const mapped = mapStrings(item, depth + 1, seen);
    if (mapped !== item) changed = true;
    next[key] = mapped;
  }
  return changed ? next : value;
};

const redactStrictUrl = (value: unknown): unknown =>
  typeof value === 'string'
    ? redactUrl(value, { blankAllQueryValues: true })
    : value;

const scrubNetworkData = (data: Plain): Plain => {
  const next: Plain = {};
  for (const key of NETWORK_CRUMB_KEYS) {
    if (key in data)
      next[key] = key === 'url' ? redactStrictUrl(data[key]) : data[key];
  }
  const unchanged =
    Object.keys(data).length === Object.keys(next).length &&
    Object.keys(next).every(key => next[key] === data[key]);
  return unchanged ? data : next;
};

export const sentryBeforeBreadcrumb = <T extends Breadcrumb>(
  breadcrumb: T
): T | null => {
  const base = receiptBeforeBreadcrumb(breadcrumb);
  // Console output carries whatever was logged -- request args, user blobs.
  if (!base || base.category === 'console') return null;

  // DOM breadcrumbs serialise aria-label/title/... into `message`, and those
  // carry amounts (e.g. an amount chooser's label).
  const redactedMessage =
    typeof base.message === 'string'
      ? base.message.replace(ATTRIBUTE_TEXT, '[$1="[redacted]"]')
      : base.message;
  const crumb =
    redactedMessage === base.message
      ? base
      : { ...base, message: redactedMessage };

  const data = crumb.data as Plain | undefined;
  if (!data) return mapStrings(crumb) as T;

  let nextData: Plain = data;
  if (base.category === 'fetch' || base.category === 'xhr') {
    nextData = scrubNetworkData(data);
  } else if (base.category === 'navigation') {
    nextData = { ...data };
    for (const key of ['from', 'to']) {
      if (key in nextData) nextData[key] = redactStrictUrl(nextData[key]);
    }
  }
  const withData = nextData === data ? crumb : { ...crumb, data: nextData };
  return mapStrings(withData) as T;
};

const REQUEST_BODY_KEYS = ['data', 'cookies', 'query_string'];

// The previous page's URL keeps query values such as a student name.
const scrubHeaders = (headers: Plain): Plain => {
  let next = headers;
  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() !== 'referer') continue;
    const scrubbed = redactStrictUrl(value);
    if (scrubbed === value) continue;
    if (next === headers) next = { ...headers };
    next[key] = scrubbed;
  }
  return next;
};

const scrubRequest = (request: Plain): Plain => {
  const hasBody = REQUEST_BODY_KEYS.some(key => key in request);
  const url = redactStrictUrl(request.url);
  const headers =
    request.headers !== null && typeof request.headers === 'object'
      ? scrubHeaders(request.headers as Plain)
      : request.headers;
  if (!hasBody && url === request.url && headers === request.headers) {
    return request;
  }
  const next: Plain = { ...request };
  for (const key of REQUEST_BODY_KEYS) delete next[key];
  if (url !== undefined) next.url = url;
  if (headers !== undefined) next.headers = headers;
  return next;
};

/**
 * The SDK builds the exception value of a non-Error throw (an RTK error
 * object, a response body) from its `message`, which is backend free text.
 * Those are marked `synthetic`; our own `toError` output is not.
 */
const scrubExceptions = (exception: Plain): Plain => {
  const values = exception.values;
  if (!Array.isArray(values)) return exception;
  let changed = false;
  const next = values.map((item: Plain) => {
    const synthetic =
      (item?.mechanism as Plain | undefined)?.synthetic === true;
    const value = item?.value;
    if (
      synthetic &&
      typeof value === 'string' &&
      !value.startsWith(NON_ERROR_PREFIX)
    ) {
      changed = true;
      return { ...item, value: NON_ERROR_PREFIX };
    }
    return item;
  });
  return changed ? { ...exception, values: next } : exception;
};

export const sentryBeforeSend = <T extends ErrorEvent>(event: T): T => {
  let next = receiptBeforeSend(event) as unknown as Plain;
  const original = next;
  const set = (key: string, value: unknown) => {
    if (next === original) next = { ...original };
    next[key] = value;
  };

  const request = next.request as Plain | undefined;
  if (request) {
    const scrubbed = scrubRequest(request);
    if (scrubbed !== request) set('request', scrubbed);
  }

  const extra = next.extra as Plain | undefined;
  if (extra) {
    const kept = Object.fromEntries(
      Object.entries(extra).filter(([key]) => ALLOWED_EXTRA_KEYS.has(key))
    );
    if (Object.keys(kept).length !== Object.keys(extra).length) {
      set('extra', kept);
    }
  }

  const contexts = next.contexts as Plain | undefined;
  if (contexts) {
    const kept = Object.fromEntries(
      Object.entries(contexts).filter(([key]) => ALLOWED_CONTEXT_KEYS.has(key))
    );
    if (Object.keys(kept).length !== Object.keys(contexts).length) {
      set('contexts', kept);
    }
  }

  const exception = next.exception as Plain | undefined;
  if (exception) {
    const scrubbed = scrubExceptions(exception);
    if (scrubbed !== exception) set('exception', scrubbed);
  }

  const breadcrumbs = next.breadcrumbs as Breadcrumb[] | undefined;
  if (Array.isArray(breadcrumbs)) {
    const scrubbed = breadcrumbs
      .map(crumb => sentryBeforeBreadcrumb(crumb))
      .filter((crumb): crumb is Breadcrumb => crumb !== null);
    const same =
      scrubbed.length === breadcrumbs.length &&
      scrubbed.every((crumb, i) => crumb === breadcrumbs[i]);
    if (!same) set('breadcrumbs', scrubbed);
  }

  return mapStrings(next) as unknown as T;
};

/** Hooks to hand to `Sentry.init`: a scrubber failure drops, never leaks. */
export const safeBeforeSend = <T extends ErrorEvent>(event: T): T | null => {
  try {
    return sentryBeforeSend(event);
  } catch {
    return null;
  }
};

export const safeBeforeBreadcrumb = <T extends Breadcrumb>(
  breadcrumb: T
): T | null => {
  try {
    return sentryBeforeBreadcrumb(breadcrumb);
  } catch {
    return null;
  }
};
